import { useTranslate } from "@tolgee/react";
import { Check, Loader2 } from "lucide-react";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "#/components/ui/tooltip";
import { cn } from "#/lib/utils";
import {
	useWithdrawalInTransit,
	useWithdrawalJustCompleted,
} from "./hooks/use-withdrawal-in-transit";

type Props = {
	className?: string;
};

/**
 * Self-gating withdrawal status icon — the mirror of deposit-warning-indicator
 * without its warning state: a spinner while a "transfer to wallet" leg is in
 * flight (~5 min), a "completed" check for a short window once the funds land,
 * otherwise null. With no triangle here, both icons use plain theme color
 * classes, so callers can override the color via `className` alone (e.g.
 * `text-primary-foreground` on a `bg-primary` button) — no separate status
 * prop like the deposit indicator needs for its arbitrary-value orange.
 */
export function WithdrawStatusIndicator({ className }: Props) {
	const { t } = useTranslate();
	const inTransit = useWithdrawalInTransit();
	const justCompleted = useWithdrawalJustCompleted();

	if (!inTransit && !justCompleted) return null;

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<span className="inline-flex">
					{inTransit ? (
						<Loader2
							className={cn(
								"h-3.5 w-3.5 shrink-0 animate-spin text-primary",
								className,
							)}
						/>
					) : (
						<Check
							className={cn("h-3.5 w-3.5 shrink-0 text-primary", className)}
						/>
					)}
				</span>
			</TooltipTrigger>
			<TooltipContent side="top" className="max-w-[240px]">
				{t(
					inTransit
						? "withdraw.awaiting-funds"
						: "transferWallet.completed-title",
				)}
			</TooltipContent>
		</Tooltip>
	);
}
